import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  HUB_HALL,
  createHubGroup,
  ensureFounderHub,
  ensureTheHub,
  formatHubHtml,
  hideHubEvent,
  hideHubPost,
  isGroupMember,
  isHubBlocked,
  isHubHall,
  isKnownHubRoom,
  joinHubGroup,
  lastHubFeedDay,
  listHubEvents,
  listHubGroups,
  listHubPosts,
  listVisibleHubRooms,
  markHubRead,
  pinHubPost,
  postHubMessage,
  recordHubVisit,
  roomKey,
  roomMeta as founderRoomMeta,
  saveHubEvent,
  searchTheHub,
  unreadByCategory,
  useTheHubLive,
} from "../../js/theHub";
import ConventionHall from "./ConventionHall";
import EventCard from "./EventCard";
import EventRequestModal from "./EventRequestModal";
import RoomMark from "./RoomMark";
import SpecialEventsModal from "./SpecialEventsModal";

export default function TheHubApp({ session, basePath }) {
  const navigate = useNavigate();
  const { category: rawCategory } = useParams();
  const [params, setParams] = useSearchParams();
  const hall = isHubHall(rawCategory);
  const category = hall ? HUB_HALL : isKnownHubRoom(rawCategory) ? rawCategory : HUB_HALL;
  const inRoom = category !== HUB_HALL;
  const groupId = params.get("group") || "";
  const [pulse, setPulse] = useState(0);
  const [query, setQuery] = useState("");
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [announce, setAnnounce] = useState(false);
  const [error, setError] = useState("");
  const [specialOpen, setSpecialOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestMode, setRequestMode] = useState("request");
  const [groupName, setGroupName] = useState("");
  const [groupSummary, setGroupSummary] = useState("");
  const [groupEvent, setGroupEvent] = useState({
    title: "",
    date: "",
    time: "",
    location: "",
    address: "",
    contact: "",
    url: "",
    description: "",
  });
  const chatRef = useRef(null);
  const lastRoom = useRef("");
  const rooms = listVisibleHubRooms();
  const roomMeta = founderRoomMeta(category);

  ensureTheHub();
  ensureFounderHub();
  useTheHubLive(() => setPulse((n) => n + 1));

  const blocked = session?.role !== "admin" && isHubBlocked(session?.userId);
  const groups = useMemo(() => listHubGroups(), [pulse]);
  const unread = useMemo(() => unreadByCategory(session), [session, pulse]);
  const feedDay = useMemo(() => lastHubFeedDay(), [pulse]);
  const events = useMemo(() => {
    if (!inRoom) return [];
    if (category === "chat" && !groupId) return listHubEvents().slice(0, 4);
    return listHubEvents({
      category: groupId ? undefined : category === "special" ? "special" : category,
      specialOnly: !groupId && category === "special",
      groupId: groupId || null,
    });
  }, [category, groupId, inRoom, pulse]);
  const posts = useMemo(
    () =>
      inRoom
        ? listHubPosts({
            category: category === "associations" && groupId ? "associations" : category,
            groupId: groupId || null,
          })
        : [],
    [category, groupId, inRoom, pulse]
  );
  const roots = posts.filter((row) => !row.parent_id);
  const repliesOf = (id) => posts.filter((row) => row.parent_id === id);
  const announcements = roots.filter((row) => row.kind === "announcement");
  const prompts = roots.filter((row) => row.kind === "prompt");
  const pinned = roots.filter((row) => row.pinned && row.kind !== "announcement" && row.kind !== "prompt");
  const talk = roots.filter((row) => row.kind !== "announcement" && row.kind !== "prompt" && !row.pinned);
  const searchHits = useMemo(() => (query.trim() ? searchTheHub(query) : null), [query, pulse]);
  const activeGroup = groups.find((row) => row.id === groupId) || null;
  const memberOfActive = Boolean(activeGroup && (session?.role === "admin" || isGroupMember(activeGroup.id, session?.userId)));
  const canAnnounce = session?.role === "broker" || session?.role === "admin";
  const key = roomKey({ category, groupId: groupId || null });
  const latestAt = posts.reduce((max, row) => (row.created_at > max ? row.created_at : max), "");
  const open = !activeGroup || memberOfActive;

  useEffect(() => {
    if (rawCategory && rawCategory !== category && !isKnownHubRoom(rawCategory)) {
      navigate(`${basePath}/${HUB_HALL}`, { replace: true });
    }
  }, [rawCategory, category, basePath, navigate]);

  useEffect(() => {
    if (!inRoom) return;
    markHubRead(session, key, latestAt);
  }, [session, key, latestAt, inRoom]);

  useEffect(() => {
    const node = chatRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [posts.length, category, groupId]);

  useEffect(() => {
    if (!inRoom) {
      lastRoom.current = "";
      return;
    }
    if (lastRoom.current === category) return;
    recordHubVisit(session?.userId, category, lastRoom.current);
    lastRoom.current = category;
  }, [category, inRoom, session?.userId]);

  function go(nextCategory, nextGroup = "") {
    setReplyTo(null);
    setError("");
    setParams(nextGroup ? { group: nextGroup } : {});
    navigate(`${basePath}/${nextCategory}${nextGroup ? `?group=${nextGroup}` : ""}`);
  }

  function send(event) {
    event.preventDefault();
    setError("");
    try {
      postHubMessage({
        session,
        category: groupId ? "associations" : category,
        groupId: groupId || null,
        parentId: replyTo?.id || null,
        body,
        kind: announce ? "announcement" : "message",
      });
      setBody("");
      setReplyTo(null);
      setAnnounce(false);
    } catch (err) {
      setError(err.message || "That note could not be posted.");
    }
  }

  function createOfficeEvent(event) {
    event.preventDefault();
    setError("");
    try {
      saveHubEvent({ ...groupEvent, group_id: groupId, type: "other", special: false }, session);
      setGroupEvent({
        title: "",
        date: "",
        time: "",
        location: "",
        address: "",
        contact: "",
        url: "",
        description: "",
      });
    } catch (err) {
      setError(err.message || "That listing could not be saved.");
    }
  }

  function createGroup(event) {
    event.preventDefault();
    setError("");
    try {
      const group = createHubGroup({ session, name: groupName, summary: groupSummary });
      setGroupName("");
      setGroupSummary("");
      go("associations", group.id);
    } catch (err) {
      setError(err.message || "The circle could not be created.");
    }
  }

  function renderPost(row, prompt = false) {
    return (
      <li
        key={row.id}
        id={prompt ? `hub-thread-${row.id}` : undefined}
        className={`the-hub-post${row.pinned ? " is-pinned" : ""}${row.kind === "announcement" ? " is-announce" : ""}${
          prompt ? " is-prompt" : ""
        }`}
      >
        <header>
          <strong>{row.author_name}</strong>
          <span>{row.author_role}</span>
          {row.kind === "announcement" ? <span>Announcement</span> : null}
          {row.kind === "prompt" ? <span>Room thread</span> : null}
          {row.pinned ? <span>Pinned</span> : null}
        </header>
        <p dangerouslySetInnerHTML={{ __html: formatHubHtml(row.body) }} />
        <div className="the-hub-post__tools">
          <button type="button" className="text-btn" onClick={() => setReplyTo(row)}>
            Reply
          </button>
          {session.role === "admin" && row.kind !== "prompt" ? (
            <>
              <button type="button" className="text-btn" onClick={() => pinHubPost(row.id, session, !row.pinned)}>
                {row.pinned ? "Unpin" : "Pin"}
              </button>
              <button type="button" className="text-btn" onClick={() => hideHubPost(row.id, session)}>
                Remove
              </button>
            </>
          ) : null}
        </div>
        {repliesOf(row.id).length ? (
          <ol className="the-hub-replies">
            {repliesOf(row.id).map((reply) => (
              <li key={reply.id}>
                <strong>{reply.author_name}</strong>
                <p dangerouslySetInnerHTML={{ __html: formatHubHtml(reply.body) }} />
                {session.role === "admin" ? (
                  <button type="button" className="text-btn" onClick={() => hideHubPost(reply.id, session)}>
                    Remove
                  </button>
                ) : null}
              </li>
            ))}
          </ol>
        ) : null}
      </li>
    );
  }

  if (blocked) {
    return (
      <article className="the-hub the-hub--paused">
        <h1>The Hub</h1>
        <p>Your Hub access is paused. Please write to support if this is a surprise.</p>
      </article>
    );
  }

  return (
    <div className={`the-hub${inRoom ? " the-hub--room" : " the-hub--hall"}`}>
      <header className="the-hub__top">
        <div>
          <p className="eyebrow">Private professional space</p>
          <h1>The Hub</h1>
        </div>
        <label className="the-hub-search">
          <span className="sr-only">Search The Hub</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search chats, events, and rooms"
          />
        </label>
        <div className="the-hub-top-actions">
          {inRoom ? (
            <button type="button" className="ghost-btn" onClick={() => go(HUB_HALL)}>
              Convention Hall
            </button>
          ) : null}
          <button type="button" className="ghost-btn" onClick={() => setSpecialOpen(true)}>
            Special Events
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              setRequestMode("request");
              setRequestOpen(true);
            }}
          >
            Request a listing
          </button>
        </div>
      </header>

      <div className="the-hub__stage" ref={inRoom ? undefined : undefined}>
        {searchHits ? (
          <section className="the-hub-search-hits">
            <h2>Search</h2>
            {!searchHits.posts.length && !searchHits.events.length && !searchHits.categories.length && !searchHits.groups.length ? (
              <p className="muted">Nothing matched that yet.</p>
            ) : null}
            {searchHits.categories.map((item) => (
              <button key={item.id} type="button" className="text-btn" onClick={() => go(item.id)}>
                Room: {item.label}
              </button>
            ))}
            {searchHits.groups?.map((item) => (
              <button key={item.id} type="button" className="text-btn" onClick={() => go("associations", item.id)}>
                Circle: {item.name}
              </button>
            ))}
            {searchHits.events.map((item) => (
              <button
                key={item.id}
                type="button"
                className="text-btn"
                onClick={() => go(item.group_id ? "associations" : item.category, item.group_id || "")}
              >
                Listing: {item.title}
              </button>
            ))}
            {searchHits.posts.slice(0, 8).map((item) => (
              <button
                key={item.id}
                type="button"
                className="text-btn"
                onClick={() => go(item.category, item.group_id || "")}
              >
                Chat: {item.body.slice(0, 72)}
              </button>
            ))}
          </section>
        ) : null}

        {!inRoom ? <ConventionHall unread={unread} onEnter={(id) => go(id)} /> : null}

        {inRoom ? (
          <div className="hub-room">
            <header className="hub-room__head">
              <RoomMark roomId={category} size={72} />
              <div>
                <p className="the-hub-lead">{activeGroup ? activeGroup.name : roomMeta?.label}</p>
                <h2>{activeGroup ? "Office circle" : "Room"}</h2>
                <p className="muted">{activeGroup?.summary || roomMeta?.blurb}</p>
                <p className="hub-room__fresh">
                  Listings refreshed {feedDay}. Showing today through the next 60 days.
                </p>
              </div>
              <nav className="hub-room__jump" aria-label="Other rooms">
                {rooms.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`hub-room-chip${item.id === category && !groupId ? " is-on" : ""}`}
                    onClick={() => go(item.id)}
                    title={item.label}
                  >
                    <RoomMark roomId={item.id} size={28} />
                  </button>
                ))}
              </nav>
            </header>

            {category === "associations" ? (
              <section className="the-hub-groups">
                <h3>Office circles</h3>
                <ul>
                  {groups.map((group) => (
                    <li key={group.id}>
                      <button
                        type="button"
                        className={`the-hub-group${groupId === group.id ? " is-on" : ""}`}
                        onClick={() => go("associations", group.id)}
                      >
                        <strong>{group.name}</strong>
                        <span>{group.summary || "Office circle"}</span>
                        {unread[`group:${group.id}`] ? <em>{unread[`group:${group.id}`]}</em> : null}
                      </button>
                      {session.role === "agent" && !isGroupMember(group.id, session.userId) ? (
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => {
                            try {
                              joinHubGroup({ session, groupId: group.id });
                              setPulse((n) => n + 1);
                            } catch (err) {
                              setError(err.message);
                            }
                          }}
                        >
                          Join
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {canAnnounce ? (
                  <form className="the-hub-mini" onSubmit={createGroup}>
                    <p>Create a circle for your brokerage.</p>
                    <input
                      className="field-input"
                      value={groupName}
                      onChange={(event) => setGroupName(event.target.value)}
                      placeholder="Circle name"
                      required
                    />
                    <input
                      className="field-input"
                      value={groupSummary}
                      onChange={(event) => setGroupSummary(event.target.value)}
                      placeholder="A short note"
                    />
                    <button className="primary-btn" type="submit">
                      Create circle
                    </button>
                  </form>
                ) : null}
              </section>
            ) : null}

            {activeGroup && !memberOfActive ? (
              <p className="the-hub-join-note">
                Join this circle to read the feed and post. Circles belong to the brokerage that opened them.
              </p>
            ) : null}

            {open ? (
              <div className="hub-room__frame">
                <section className="the-hub-events">
                  <h3>
                    {category === "special"
                      ? "Admin-posted events"
                      : activeGroup
                        ? "Circle listings"
                        : category === "chat"
                          ? "Nearby in the hall"
                          : "Listings"}
                  </h3>
                  {events.length ? (
                    <ul>
                      {events.map((item) => (
                        <EventCard
                          key={item.id}
                          item={item}
                          session={session}
                          groupId={groupId}
                          onRemove={(row) => hideHubEvent(row.id, session)}
                        />
                      ))}
                    </ul>
                  ) : category === "special" ? (
                    <p>We have no custom events to share at this time.</p>
                  ) : (
                    <p className="muted">No listings in this room today.</p>
                  )}
                </section>

                {activeGroup && canAnnounce ? (
                  <form className="the-hub-mini" onSubmit={createOfficeEvent}>
                    <p>Add a listing for this circle.</p>
                    <input
                      className="field-input"
                      value={groupEvent.title}
                      onChange={(event) => setGroupEvent((current) => ({ ...current, title: event.target.value }))}
                      placeholder="Event title"
                      required
                    />
                    <div className="the-hub-form__row">
                      <input
                        className="field-input"
                        type="date"
                        value={groupEvent.date}
                        onChange={(event) => setGroupEvent((current) => ({ ...current, date: event.target.value }))}
                        required
                      />
                      <input
                        className="field-input"
                        value={groupEvent.time}
                        onChange={(event) => setGroupEvent((current) => ({ ...current, time: event.target.value }))}
                        placeholder="Time"
                      />
                    </div>
                    <input
                      className="field-input"
                      value={groupEvent.location}
                      onChange={(event) => setGroupEvent((current) => ({ ...current, location: event.target.value }))}
                      placeholder="Venue or room"
                    />
                    <input
                      className="field-input"
                      value={groupEvent.address}
                      onChange={(event) => setGroupEvent((current) => ({ ...current, address: event.target.value }))}
                      placeholder="Street address"
                    />
                    <input
                      className="field-input"
                      value={groupEvent.contact}
                      onChange={(event) => setGroupEvent((current) => ({ ...current, contact: event.target.value }))}
                      placeholder="Contact (phone or email)"
                    />
                    <input
                      className="field-input"
                      type="url"
                      value={groupEvent.url}
                      onChange={(event) => setGroupEvent((current) => ({ ...current, url: event.target.value }))}
                      placeholder="Source link (https://)"
                    />
                    <input
                      className="field-input"
                      value={groupEvent.description}
                      onChange={(event) => setGroupEvent((current) => ({ ...current, description: event.target.value }))}
                      placeholder="A short description"
                    />
                    <button className="ghost-btn" type="submit">
                      Save circle listing
                    </button>
                  </form>
                ) : null}

                {announcements.length ? (
                  <section>
                    <h3>Announcements</h3>
                    <ol className="the-hub-feed">{announcements.map((row) => renderPost(row))}</ol>
                  </section>
                ) : null}

                {pinned.length ? (
                  <section>
                    <h3>Pinned</h3>
                    <ol className="the-hub-feed">{pinned.map((row) => renderPost(row))}</ol>
                  </section>
                ) : null}

                {prompts.length ? (
                  <section>
                    <h3>Room threads</h3>
                    <ol className="the-hub-feed">{prompts.map((row) => renderPost(row, true))}</ol>
                  </section>
                ) : null}

                {category !== "special" ? (
                  <>
                    <section id="hub-room-conversation">
                      <h3>Conversation</h3>
                      <ol className="the-hub-feed the-hub-feed--live" ref={chatRef}>
                        {(searchHits?.posts.length
                          ? searchHits.posts.filter((row) => !row.parent_id && row.kind === "message")
                          : talk
                        ).map((row) => renderPost(row))}
                      </ol>
                    </section>
                    <form className="the-hub-compose" onSubmit={send}>
                      {replyTo ? (
                        <p className="muted">
                          Replying to {replyTo.author_name}.{" "}
                          <button type="button" className="text-btn" onClick={() => setReplyTo(null)}>
                            Cancel
                          </button>
                        </p>
                      ) : null}
                      <label>
                        <span className="sr-only">Write a note</span>
                        <textarea
                          rows={3}
                          value={body}
                          onChange={(event) => setBody(event.target.value)}
                          placeholder="A short, useful note. No images, attachments, or emojis."
                        />
                      </label>
                      {canAnnounce ? (
                        <label className="the-hub-announce">
                          <input type="checkbox" checked={announce} onChange={(event) => setAnnounce(event.target.checked)} />
                          Post as an announcement
                        </label>
                      ) : null}
                      {error ? <p className="the-hub-error">{error}</p> : null}
                      <button className="primary-btn" type="submit">
                        Post
                      </button>
                    </form>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <footer className="the-hub__foot">
        <p>
          Event listings and group information are provided as a convenience for our members. While we
          strive to keep everything accurate and up-to-date, details may change. Events are updated daily
          from verified sources. If you notice incorrect hours, dates, times, or locations, please{" "}
          <button
            type="button"
            className="text-btn"
            onClick={() => {
              setRequestMode("correction");
              setRequestOpen(true);
            }}
          >
            click here
          </button>{" "}
          to request an update.
        </p>
      </footer>

      <SpecialEventsModal isOpen={specialOpen} onClose={() => setSpecialOpen(false)} session={session} />
      <EventRequestModal
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        session={session}
        mode={requestMode}
      />
    </div>
  );
}
